<script lang="ts">
	// The design system's Keypad and Key (design/components/core/Keypad.jsx).
	//
	// Two layouts. number is calculator order, 7-8-9 on top, a double-width 0
	// and a backspace. pairing is the 32 characters of a pairing code, 2-9 and
	// A-Z without 0, O, 1 and I, with the backspace ending the digit row.
	// Keys are --key-w by --key-h, --touch-gap apart, and the grid is always
	// left to right: a number pad is a physical convention, not text.
	//
	// The press is the design's invert: the key turns to the emphasis surface
	// the instant a finger lands and nothing moves. Disabled keys take the one
	// disabled look from interaction.css.
	//
	// The pad reports keys and nothing else, as in the design. What the keys
	// build, a passcode or an amount, belongs to the screen that uses it; the
	// screen says whether another key may be typed (canType) or deleted
	// (canDelete). testId is the app's: each key is `${testId}-key-${key}` and
	// the backspace `${testId}-backspace`.
	//
	// Inside a Sheet the pad never scrolls. `fit` makes its four rows share the
	// height the caller gives it, each between --tap-min and --key-h, so the
	// worst case is shorter than any sheet body at 1024x600. The caller gives
	// the pad an element with a height, such as flex: 1 1 0 in a column.

	import Icon from './Icon.svelte';
	import { pressable } from './pressable';

	interface Props {
		testId: string;
		/** What the pad is for, for a screen reader. */
		label: string;
		/** The backspace's name, for a screen reader. */
		deleteLabel: string;
		onKey: (key: string) => void;
		onBackspace: () => void;
		layout?: 'number' | 'pairing';
		disabled?: boolean;
		canType?: boolean;
		canDelete?: boolean;
		/** Share the height given instead of taking --key-h per row. */
		fit?: boolean;
	}

	let {
		testId,
		label,
		deleteLabel,
		onKey,
		onBackspace,
		layout = 'number',
		disabled = false,
		canType = true,
		canDelete = true,
		fit = false,
	}: Props = $props();

	type Cell =
		{ kind: 'key'; key: string; span: number } | { kind: 'back' } | { kind: 'gap'; row: number };

	const NUMBER = ['7', '8', '9', '4', '5', '6', '1', '2', '3'];
	const PAIR_DIGITS = ['2', '3', '4', '5', '6', '7', '8', '9'];
	const PAIR_LETTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ'.split('');

	const pairing = $derived(layout === 'pairing');
	const cells = $derived.by((): Cell[] => {
		const key = (k: string, span = 1): Cell => ({ kind: 'key', key: k, span });
		if (!pairing) return [...NUMBER.map((k) => key(k)), key('0', 2), { kind: 'back' }];
		// The three letter rows leave the ninth column empty rather than
		// reflowing.
		const out: Cell[] = [...PAIR_DIGITS.map((k) => key(k)), { kind: 'back' }];
		for (let row = 0; row < 3; row++) {
			out.push(...PAIR_LETTERS.slice(row * 8, row * 8 + 8).map((k) => key(k)));
			out.push({ kind: 'gap', row });
		}
		return out;
	});
	const typeOff = $derived(disabled || !canType);
</script>

<div
	class="pad"
	class:pairing
	class:fit
	dir="ltr"
	role="group"
	aria-label={label}
	aria-disabled={disabled || undefined}
	data-testid={testId}
>
	{#each cells as cell (cell.kind === 'key' ? cell.key : cell.kind === 'gap' ? `gap-${cell.row}` : 'back')}
		{#if cell.kind === 'key'}
			<button
				type="button"
				class="key"
				class:wide={cell.span > 1}
				data-testid="{testId}-key-{cell.key}"
				data-press="invert"
				disabled={typeOff}
				onclick={() => onKey(cell.key)}
				{@attach pressable}
			>
				{cell.key}
			</button>
		{:else if cell.kind === 'back'}
			<button
				type="button"
				class="key"
				data-testid="{testId}-backspace"
				aria-label={deleteLabel}
				data-press="invert"
				disabled={disabled || !canDelete}
				onclick={onBackspace}
				{@attach pressable}
			>
				<!-- Icon's backspace, which points left in both languages: the pad
				     never mirrors. -->
				<Icon name="backspace" size={28} />
			</button>
		{:else}
			<span aria-hidden="true"></span>
		{/if}
	{/each}
</div>

<style>
	.pad {
		display: grid;
		grid-template-columns: repeat(3, var(--key-w));
		grid-auto-rows: var(--key-h);
		gap: var(--touch-gap);
		inline-size: max-content;
	}

	.pad.fit {
		grid-template-rows: repeat(4, minmax(var(--tap-min), var(--key-h)));
		grid-auto-rows: unset;
		block-size: 100%;
		align-content: start;
	}

	.pad.pairing {
		grid-template-columns: repeat(9, var(--key-w));
	}

	.key {
		display: grid;
		place-items: center;
		min-inline-size: 0;
		padding: 0;
		background: var(--surface-card);
		color: var(--text-strong);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-tile);
		font-family: var(--font-mono);
		font-size: 28px;
		font-weight: 600;
		line-height: 1;
		cursor: pointer;
	}

	.pairing .key {
		font-size: 24px;
	}

	.key.wide {
		grid-column: span 2;
	}
</style>
